/**
 * Timeline store — PWA-side canonical library for the three-tier timeline
 * family (Action / Song / Genre). Milestone 1 scope: schema + localStorage
 * library + minimal editor. No firing yet — runtime lives on OPBridge (cross-
 * project ask filed in OPApps/opbridge-opbuttons.md; see DEC-024).
 *
 * Storage:
 *   localStorage key TIMELINES_KEY. Single library - all three tiers in one
 *   document, discriminated by `tier` (DEC-032). Since plan 3.1 (2026-10-01)
 *   each timeline belongs to a project (`projectId`) and travels between
 *   devices inside that project's sync document; the rules are in
 *   `librarySync.ts`, the transport in `layout/projectSync.ts`. Lists show the
 *   active project's timelines (`timelinesInActiveProject`).
 *
 * Runtime coupling in M1: NONE. Bridge push happens in Milestone 2 when the
 * OPBridge timeline engine ships.
 */

import { createEffect, createSignal, on, type Accessor } from 'solid-js';
import {
  type Timeline,
  type TimelineLibrary,
  type TimelineTier,
  type TimelineLane,
  type TimelineLaneKind,
  type TimelineSection,
  type SectionOnEnd,
  type TimelineArrival,
  type TimelineStep,
  type ButtonRefStep,
  type TargetValueStep,
  type ValuePointCurve,
  type ColorMarkerStep,
  type ColorInterpolationSpace,
  type HuePathDirection,
  type TimelineSongClip,
  type PatternStep,
  type PatternKey,
  unrollPattern,
  TIMELINES_SCHEMA_VERSION,
  TIMELINE_DEFAULT_SECTION_BARS,
  TIMELINE_MAX_BARS_SONG_ACTION,
  timelineLoops,
  timelineLeadIn,
  isButtonRefStep,
} from '@opbuttons/schema';
import { ulid } from '../utils/ulid.ts';
import { kindOf } from './songParts.ts';
import { editLocked, leaseFor } from './lease.ts';
import { pushToast } from '../ui/toast.ts';
import { keepLightingHonest, lightingCopyOf, lightingCopyName, withoutLighting, canCopyAsLighting } from './lighting.ts';
import { fixtures, getFixture } from '../fixtures/store.ts';
import { createHistory, type HistoryHandle } from '../undo/history.ts';
import { noteEdit, registerOrdered } from '../undo/editOrder.ts';
import { activeProjectId, projects } from '../layout/projectStore.ts';
import {
  claimUnownedTimelines, mergeTimelines, stampChangedTimelines, timelineInProject,
  type TimelineMergeResult, type TimelineTombstone,
} from './librarySync.ts';

const TIMELINES_KEY = 'opbuttons-timelines-v1';
/** Timelines this device knows were deleted (here or on another device), so
 *  project sync can tell "deleted" from "never seen". `librarySync.ts`. */
const TOMBSTONES_KEY = 'opbuttons-timelines-tombstones-v1';

function emptyLibrary(): TimelineLibrary {
  return { schemaVersion: TIMELINES_SCHEMA_VERSION, timelines: [] };
}

/** Fresh timeline with one section and NO lanes (2026-08-04 — was seeded
 *  with v1 default lanes; operator asked for a blank slate). The editor's
 *  empty state prompts "add a lane" instead of pre-deciding what the
 *  operator needs. `tier` drives the section length default (§10 Q4 + Q6
 *  answers 2026-07-01). */
export function makeBlankTimeline(tier: TimelineTier, name: string): Timeline {
  const now = new Date().toISOString();
  const section: TimelineSection = {
    id: ulid(),
    name: tier === 'genre' ? 'LOOP' : tier === 'action' ? 'ACTION' : 'INTRO',
    lengthBars: TIMELINE_DEFAULT_SECTION_BARS[tier],
    // DEC-227: a Song's sections loop until the performer moves on (supersedes DEC-034's Song default of
    // 'auto-advance', 2026-10-09). Action plays once, Genre loops.
    onEnd: tier === 'action' ? 'stop' : 'loop',
  };
  return {
    id: ulid(),
    name: name.trim() || 'Untitled Timeline',
    tier,
    sections: [section],
    lanes: [],
    defaultSnap: 'bar',
    signature: { num: 4, den: 4 },
    // Made here: OPButtons, for good (the app logo on the timeline says so; never changed by either app).
    origin: 'opbuttons',
    created: now,
    modified: now,
  };
}

/** Keep saved timelines this build cannot read, and say so (2026-10-16). `load()` used to hand back an empty library
 *  for a corrupted save or one from a newer version, and the very next edit wrote that empty library over it, so the
 *  "leave the on-disk doc untouched" it promised lasted one edit. The unreadable text is copied to its own dated key
 *  first, where nothing writes; the library starts empty and fills again from the projects' sync. */
function setAside(raw: string, why: string): TimelineLibrary {
  const key = `${TIMELINES_KEY}.unreadable-${new Date().toISOString().replace(/[:.]/g, '-')}`;
  let kept = false;
  try { localStorage.setItem(key, raw); kept = true; } catch { /* full: the message says so */ }
  console.warn(`timelines: ${why}; ${kept ? `kept as ${key}` : 'could NOT be kept (storage full)'}`);
  pushToast('error', kept
    ? `Saved timelines on this device could not be read (${why}). They are kept aside, not lost; your projects' timelines come back from OPBridge.`
    : `Saved timelines on this device could not be read (${why}), and there was no room to keep a copy.`);
  return emptyLibrary();
}

function load(): TimelineLibrary {
  if (typeof localStorage === 'undefined') return emptyLibrary();
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(TIMELINES_KEY);
    if (!raw) return emptyLibrary();
    const parsed = JSON.parse(raw) as TimelineLibrary;
    if (parsed.schemaVersion !== TIMELINES_SCHEMA_VERSION) return setAside(raw, `saved by a newer version (${String(parsed.schemaVersion)})`);
    if (!Array.isArray(parsed.timelines)) return setAside(raw, 'not in the expected shape');
    return { ...parsed, timelines: parsed.timelines.map(dropLegacyFixtureLanes) };
  } catch (err) {
    console.warn('timelines: load failed', err);
    return raw ? setAside(raw, 'damaged') : emptyLibrary();
  }
}

/** Tolerance for data authored before the 2026-08-04 lane redesign: the old
 *  `fixture-group` / `fixture-cap` / `fixture-color` sub-lane system (and
 *  its `valuePoint` steps) is gone from the schema, so a saved timeline
 *  that still has one would otherwise render lanes the editor no longer
 *  understands. Rather than crash or silently keep unrenderable lanes
 *  around forever, this drops ONLY the legacy fixture lanes on load —
 *  every other lane (MIDI/OSC/Marker/mixed/new DMX kinds) and the rest of
 *  the timeline (sections, other lanes' steps) is untouched. Not a data
 *  migration (there's no lossless mapping from per-capability automation
 *  keyframes onto a flat FX+colour lane) — a one-way drop, logged so it's
 *  never silent. Idempotent — re-running is a no-op once the legacy lanes
 *  are gone. */
function dropLegacyFixtureLanes(t: Timeline): Timeline {
  const LEGACY_KINDS = new Set(['fixture-group', 'fixture-cap', 'fixture-color']);
  const legacy = t.lanes.filter((l) => LEGACY_KINDS.has(l.laneKind ?? ''));
  if (legacy.length === 0) return t;
  console.warn(
    `timeline "${t.name}" (${t.id}): dropping ${legacy.length} lane(s) from the retired ` +
    'fixture-group/fixture-cap/fixture-color system (superseded by dmx-rig/dmx-fixture ' +
    `lanes, 2026-08-04): ${legacy.map((l) => `"${l.name}"`).join(', ')}`,
  );
  return { ...t, lanes: t.lanes.filter((l) => !LEGACY_KINDS.has(l.laneKind ?? '')) };
}

/** A failed save is said once (2026-10-16: it was only logged, so on a full iPad every edit lived in memory alone and
 *  was gone at the next reload). Cleared by the next save that works. */
let persistFailed = false;
function persist(lib: TimelineLibrary): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(TIMELINES_KEY, JSON.stringify(lib));
    persistFailed = false;
  } catch (err) {
    console.warn('timelines: persist failed', err);
    if (!persistFailed) pushToast('error', 'Timeline changes could not be saved on this device: its storage is full. They still travel with your project to OPBridge; free some space and they save here again.');
    persistFailed = true;
  }
}

const [library, setLibrary] = createSignal<TimelineLibrary>(load());

export const timelineLibrary: Accessor<TimelineLibrary> = library;
export const timelines: Accessor<Timeline[]> = () => library().timelines;

/** The active project's timelines - what every list shows (plan 3.1). A
 *  timeline from another project can still be opened by id (a Launcher that
 *  points at one), it just is not listed here. */
export const timelinesInActiveProject: Accessor<Timeline[]> = () => {
  const pid = activeProjectId();
  return library().timelines.filter((t) => timelineInProject(t, pid));
};

function loadTombstones(): TimelineTombstone[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(TOMBSTONES_KEY) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((t): t is TimelineTombstone =>
      typeof t === 'object' && t !== null
      && typeof (t as TimelineTombstone).id === 'string'
      && typeof (t as TimelineTombstone).deletedAt === 'number');
  } catch {
    return [];
  }
}

const [tombstoneList, setTombstoneList] = createSignal<TimelineTombstone[]>(loadTombstones());

/** Deletion records, for project sync to carry. Reactive. */
export const timelineTombstones: Accessor<TimelineTombstone[]> = tombstoneList;

function persistTombstones(next: TimelineTombstone[]): void {
  setTombstoneList(next);
  if (typeof localStorage === 'undefined') return;
  try { localStorage.setItem(TOMBSTONES_KEY, JSON.stringify(next)); } catch { /* quota - non-fatal */ }
}

/**
 * Every LOCAL write to the library goes through here, undo and redo included.
 *
 * - Stamps `modified` on what changed, so sync can tell this copy is newer.
 * - A timeline that disappears is recorded as deleted. Not only the Delete
 *   button: an undo of a create removes one too, and a timeline that had
 *   already synced would otherwise come straight back from the project doc.
 * - A timeline that exists again (undo of a delete) is no longer deleted.
 */
function writeLocal(next: TimelineLibrary): void {
  const prev = library().timelines;
  // A lighting timeline that gained a non-light lane or a song is a full timeline now (DEC-323): said once, never silent.
  const honest = keepLightingHonest(next.timelines);
  for (const d of honest.demoted) pushToast('ok', `"${d.name}" is a full timeline now: ${d.why}. It moved to Songs.`, 4000);
  const stamped = { ...next, timelines: stampChangedTimelines(prev, honest.timelines, new Date().toISOString()) };
  setLibrary(stamped);
  persist(stamped);
  const live = new Set(stamped.timelines.map((t) => t.id));
  const now = Date.now();
  const gone = prev.filter((t) => !live.has(t.id)).map((t) => t.id);
  const tombs = tombstoneList();
  if (gone.length === 0 && !tombs.some((t) => live.has(t.id))) return;
  const goneSet = new Set(gone);
  persistTombstones([
    ...tombs.filter((t) => !live.has(t.id) && !goneSet.has(t.id)),
    ...gone.map((id) => ({ id, deletedAt: now })),
  ]);
}

// ─── Undo / redo ─────────────────────────────────────────────────
//
// Migrated 2026-08-11 onto the shared `undo/history.ts` module — this
// file's own hand-rolled snapshot/batch implementation WAS the DEC-048
// pattern createHistory.ts later factored out for every other store to
// reuse; timeline itself just never got moved over until now. Behavior
// is unchanged: every mutation checkpoints the pre-state, drag-in-
// progress batches collapse into one undo entry, depth cap 100, scope is
// library snapshots only (playback/UI ephemera aren't the doc).
//
// What's NEW: timeline now registers in the global history registry
// (`historyByMostRecent()`), so a Ctrl+Z pressed OUTSIDE the Timeline
// editor can reach a timeline edit if it was the most recent one —
// previously impossible, since timeline's undo lived entirely outside
// the registry. TimelineEditor.tsx's own scoped Ctrl+Z handler
// (stopPropagation, calls `undo`/`redo` below directly) is UNCHANGED —
// it still bypasses the global most-recently-edited router while the
// editor is open, which is deliberate: editing Timeline should always
// undo Timeline, not whatever else was touched last elsewhere in the app.

export const timelineHistory = createHistory<TimelineLibrary>({
  name: 'timeline',
  read: () => library(),
  write: (next) => writeLocal(next),
  onCheckpoint: () => noteEdit('timeline'),
});
registerOrdered('timeline', timelineHistory as HistoryHandle<unknown>);

export const undoDepth: Accessor<number> = timelineHistory.undoDepth;
export const redoDepth: Accessor<number> = timelineHistory.redoDepth;
export const beginBatch: () => void = timelineHistory.beginBatch;
export const endBatch: () => void = timelineHistory.endBatch;
export const undo: () => boolean = timelineHistory.undo;
export const redo: () => boolean = timelineHistory.redo;
export const clearUndoHistory: () => void = timelineHistory.clear;

/** All timelines filtered to a specific tier. UI uses this for picker lists
 *  (button-target picks from action, genre switcher from genre, etc.). */
export function timelinesByTier(tier: TimelineTier): Timeline[] {
  return library().timelines.filter((t) => t.tier === tier);
}

/** Look up by id. Undefined if not found. */
export function getTimeline(id: string): Timeline | undefined {
  return library().timelines.find((t) => t.id === id);
}

/** Replace the whole library (a local write - see `writeLocal`). */
export function setTimelineLibrary(next: TimelineLibrary): void {
  writeLocal(next);
}

/**
 * Adopt timelines and deletion records that project sync brought from other
 * devices (`librarySync.ts::mergeTimelines`). NOT a local edit: no stamp and
 * no undo checkpoint, so the sender's `modified` survives.
 *
 * When it changes the library, the timeline undo history is CLEARED. The
 * history holds whole-library snapshots, so a Ctrl+Z after an iPad edit
 * arrived would restore the pre-arrival snapshot and silently revert the
 * iPad's edit, on every device. Losing local undo across a remote change is
 * the smaller cost.
 */
export function applySyncedTimelines(
  incoming: readonly Timeline[],
  incomingTombstones: readonly TimelineTombstone[],
): TimelineMergeResult {
  const cur = library();
  const res = mergeTimelines(cur.timelines, tombstoneList(), incoming, incomingTombstones, Date.now());
  if (res.timelines) {
    const next = { ...cur, timelines: res.timelines };
    setLibrary(next);
    persist(next);
    timelineHistory.clear();
  }
  if (JSON.stringify(res.tombstones) !== JSON.stringify(tombstoneList())) persistTombstones(res.tombstones);
  return res;
}

/** Give every timeline without a (still existing) project an owner -
 *  `librarySync.ts::claimUnownedTimelines`. A local change, so it stamps and
 *  syncs, but it is bookkeeping, not an edit: no undo entry. */
export function claimTimelineOwners(): boolean {
  const next = claimUnownedTimelines(library().timelines, projects(), activeProjectId());
  if (!next) return false;
  writeLocal({ ...library(), timelines: next });
  return true;
}

/** Keep every timeline owned: at boot (the one-time import of a device's
 *  existing library), when projects arrive or are deleted, and when a
 *  timeline is added with no project yet. Call once at app boot, inside the
 *  app's reactive root. Runs whether or not project sync is on. */
export function installTimelineOwnership(): void {
  createEffect(on(() => [projects(), activeProjectId(), library()] as const, () => { claimTimelineOwners(); }));
}

/** Insert or replace a single timeline. Bumps `modified` on replace.
 *  Snapshots for undo before mutating (respects active batch). */
export function upsertTimeline(timeline: Timeline): void {
  // Refused BEFORE the undo step (audit 2026-11-03 D-13): an edit the lease
  // refused still checkpointed, so Ctrl+Z undid nothing and redo was wiped.
  if (editLocked(timeline.id)) { sayLocked(timeline.id); return; }
  timelineHistory.checkpoint();
  upsertTimelineSilent(timeline);
}

/** Said once per 3 s, not per drag frame: a drag refused by the lease asked on every move. */
let lockedSaidAt = 0;
function sayLocked(timelineId: string): void {
  const now = Date.now();
  if (now - lockedSaidAt < 3000) return;
  lockedSaidAt = now;
  pushToast('warn', `${leaseFor(timelineId)?.holderName ?? 'Another device'} is editing this timeline. Take over to edit it here.`);
}

/** Insert or replace a timeline WITHOUT snapshotting for undo. Used by
 *  transient edits (typing in a name input) where per-keystroke undo
 *  entries would flood history. The blur/commit path uses the normal
 *  `upsertTimeline` to close out with one entry per name change. */
function upsertTimelineSilent(timeline: Timeline): void {
  // Locked out by the edit lease (S329): another device is editing this timeline; this one does not change it.
  if (editLocked(timeline.id)) { sayLocked(timeline.id); return; }
  const now = new Date().toISOString();
  const lib = library();
  const ix = lib.timelines.findIndex((t) => t.id === timeline.id);
  const withStamp: Timeline = { ...timeline, modified: now };
  const nextTimelines = ix === -1
    ? [...lib.timelines, withStamp]
    : lib.timelines.map((t, i) => (i === ix ? withStamp : t));
  setTimelineLibrary({ ...lib, timelines: nextTimelines });
}

/** Remove a timeline. */
export function removeTimeline(id: string): void {
  timelineHistory.checkpoint();
  const lib = library();
  setTimelineLibrary({ ...lib, timelines: lib.timelines.filter((t) => t.id !== id) });
}

/** Create + insert a blank timeline for the given tier. Returns the new
 *  timeline (useful for the editor to select it immediately). */
export function createTimeline(tier: TimelineTier, name: string): Timeline {
  const pid = activeProjectId();
  const t = { ...makeBlankTimeline(tier, name), ...(pid ? { projectId: pid } : {}) };
  upsertTimeline(t);
  return t;
}

/** Create + insert a blank CHAIN (2026-10-23, `timeline/chain.ts`): an Action timeline whose one lane runs its steps
 *  in order, with waits between. Starts empty; the Timelines section's chain list adds the steps. */
export function createChain(name: string): Timeline {
  const pid = activeProjectId();
  const base = makeBlankTimeline('action', name);
  const t: Timeline = { ...base, form: 'chain', lanes: [{ id: ulid(), name: 'Chain', laneKind: 'mixed', steps: [] }], ...(pid ? { projectId: pid } : {}) };
  upsertTimeline(t);
  return t;
}

/** Create + insert a blank LIGHTING timeline (DEC-323): a Song timeline with `purpose: 'lights'`, so its editor offers
 *  light lanes only and the Timelines section lists it under Lighting. */
export function createLightingTimeline(name: string): Timeline {
  const pid = activeProjectId();
  const t: Timeline = { ...makeBlankTimeline('song', name), purpose: 'lights', ...(pid ? { projectId: pid } : {}) };
  upsertTimeline(t);
  return t;
}

/** "Copy as lighting timeline" (DEC-323): a new lighting timeline holding `timelineId`'s light lanes, inserted after
 *  it. The original is untouched. Null when there is nothing to copy (no light lanes, a chain, already lighting). */
export function copyAsLightingTimeline(timelineId: string): Timeline | null {
  const t = getTimeline(timelineId);
  if (!t || !canCopyAsLighting(t)) return null;
  const lib = library();
  const copy = lightingCopyOf(t, lightingCopyName(stripCopySuffix(t.name), new Set(lib.timelines.map((x) => x.name))), ulid, new Date().toISOString());
  const ix = lib.timelines.findIndex((x) => x.id === timelineId);
  timelineHistory.checkpoint();
  setTimelineLibrary({ ...lib, timelines: [...lib.timelines.slice(0, ix + 1), copy, ...lib.timelines.slice(ix + 1)] });
  return copy;
}

/** "Make it a full timeline" (DEC-323): the lighting marker dropped, so every kind of lane can be added again and it
 *  lists under Songs. Undoable. */
export function makeFullTimeline(timelineId: string): void {
  const t = getTimeline(timelineId);
  if (!t || t.purpose !== 'lights') return;
  upsertTimeline(withoutLighting(t));
}

/** Strip a trailing " copy" / " copy N" suffix to recover the base name a
 *  duplicate chain started from - so duplicating a duplicate numbers off
 *  the original ("X" -> "X copy" -> "X copy 2"), never stacking suffixes
 *  ("X copy copy 2"). */
function stripCopySuffix(name: string): string {
  return name.replace(/\s+copy(?:\s+\d+)?$/i, '');
}

/** First "<base> copy" / "<base> copy N" not already used by any timeline
 *  in the library (checked library-wide, not per-tier - simplest rule that
 *  avoids same-named siblings). */
function nextCopyName(base: string, existingNames: ReadonlySet<string>): string {
  const plain = `${base} copy`;
  if (!existingNames.has(plain)) return plain;
  let n = 2;
  while (existingNames.has(`${base} copy ${n}`)) n++;
  return `${base} copy ${n}`;
}

/** Duplicate a whole timeline - fresh ids throughout (timeline, sections,
 *  lanes, steps), auto-numbered " copy" / " copy N" name (never collides
 *  with an existing timeline name), inserted directly after the original in
 *  the library. References INTO other libraries (buttonId / sequencerId /
 *  macroId on steps) are left pointing at the same shared object - only the
 *  timeline's own ids are new, same policy as `duplicateLane`. The result
 *  is a normal Timeline, renamable the same as any other. Returns the new
 *  timeline, or null if the source is missing. */
export function duplicateTimeline(timelineId: string): Timeline | null {
  const t = getTimeline(timelineId);
  if (!t) return null;
  const now = new Date().toISOString();
  const existingNames = new Set(library().timelines.map((x) => x.name));
  const name = nextCopyName(stripCopySuffix(t.name), existingNames);
  const copy: Timeline = {
    ...t,
    id: ulid(),
    name,
    sections: t.sections.map((s) => ({ ...s, id: ulid() })),
    lanes: t.lanes.map((l) => ({
      ...l,
      id: ulid(),
      steps: l.steps.map((s) => ({ ...s, id: ulid() })),
    })),
    created: now,
    modified: now,
    // A copy is a new timeline made HERE, whoever made the original.
    origin: 'opbuttons',
  };
  const lib = library();
  const ix = lib.timelines.findIndex((x) => x.id === timelineId);
  const nextTimelines = ix === -1
    ? [...lib.timelines, copy]
    : [...lib.timelines.slice(0, ix + 1), copy, ...lib.timelines.slice(ix + 1)];
  timelineHistory.checkpoint();
  setTimelineLibrary({ ...lib, timelines: nextTimelines });
  return copy;
}

// ─── Section + lane mutators (used by the M1 editor) ─────────────

/** Add a fresh section at the end of a timeline. Uses tier-appropriate
 *  default length. Returns the created section (or null if timeline gone). */
export function addSection(timelineId: string, name?: string): TimelineSection | null {
  const t = getTimeline(timelineId);
  if (!t) return null;
  if (refuseFollowing(t)) return null;
  const section: TimelineSection = {
    id: ulid(),
    name: (name ?? '').trim() || `SECTION ${t.sections.length + 1}`,
    lengthBars: TIMELINE_DEFAULT_SECTION_BARS[t.tier],
    onEnd: t.tier === 'action' ? 'stop' : 'loop', // DEC-227, as makeBlankTimeline
  };
  upsertTimeline({ ...t, sections: [...t.sections, section] });
  return section;
}

/** What a section does when the playhead reaches its end: run into the next one, replay itself until a jump
 *  moves on (DEC-227), or hold there. OPBridge plays it when it reports `timeline_section_loop`. */
export function setSectionOnEnd(timelineId: string, sectionId: string, onEnd: SectionOnEnd): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  const s = t.sections.find((x) => x.id === sectionId);
  if (!s || (s.onEnd ?? 'auto-advance') === onEnd) return;
  upsertTimeline({ ...t, sections: t.sections.map((x) => (x.id === sectionId ? { ...x, onEnd } : x)) });
}

/** How the lights arrive when a press jumps to a section (`TimelineSection.arrival`): `cut` is stored as absent. A
 *  section that opens with a slam still cuts (the compile's rule); the Inspector says so and offers no Blend. */
export function setSectionArrival(timelineId: string, sectionId: string, arrival: TimelineArrival): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  const s = t.sections.find((x) => x.id === sectionId);
  if (!s || (s.arrival ?? 'cut') === arrival) return;
  upsertTimeline({ ...t, sections: t.sections.map((x) => {
    if (x.id !== sectionId) return x;
    const { arrival: _old, ...rest } = x;
    return arrival === 'blend' ? { ...rest, arrival } : rest;
  }) });
}

/** How the lights arrive when the timeline is started (`Timeline.arrival`): `cut` is stored as absent. */
export function setTimelineArrival(timelineId: string, arrival: TimelineArrival): void {
  const t = getTimeline(timelineId);
  if (!t || (t.arrival ?? 'cut') === arrival) return;
  const { arrival: _old, ...rest } = t;
  upsertTimeline(arrival === 'blend' ? { ...rest, arrival } : rest);
}

/** Rename a section. Stores the raw value — the editor decides when to
 *  commit uppercase or backfill a default (typically on blur / Enter). */
export function renameSection(timelineId: string, sectionId: string, name: string): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  if (refuseFollowing(t)) return;
  const ix = t.sections.findIndex((s) => s.id === sectionId);
  if (ix === -1) return;
  const nextSections = t.sections.map((s, i) => (i === ix ? { ...s, name } : s));
  // Silent — per-keystroke; the blur/commit path checkpoints once.
  upsertTimelineSilent({ ...t, sections: nextSections });
}

/** Commit-time cleanup for a section name — trim + uppercase. Called on
 *  blur/Enter. If the trimmed value is empty, revert to `previous` (the
 *  value when the user started editing). Falls back to `SECTION N` only
 *  when previous itself was empty (e.g. the section was freshly created
 *  without a name — shouldn't happen in normal flow). */
export function commitSectionName(timelineId: string, sectionId: string, previous: string): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  if (refuseFollowing(t)) return;
  const ix = t.sections.findIndex((s) => s.id === sectionId);
  if (ix === -1) return;
  const cur = t.sections[ix]!;
  const trimmed = cur.name.trim().toUpperCase();
  const cleaned = trimmed || previous || `SECTION ${ix + 1}`;
  const named = (name: string) => t.sections.map((s, i) => (i === ix ? { ...s, name } : s));
  // ONE UNDO STEP PER RENAME (2026-10-16). Typing saves silently, so by
  // now the store already holds the typed name and an early "nothing to
  // tidy" return recorded no step at all: no name edit could be undone
  // (found building the timeline editor's in-place rename). Put the name it
  // had before typing back silently, so the undo-tracked write's checkpoint
  // holds it; a rename that ends where it started records nothing.
  if (cleaned === previous) { if (cur.name !== cleaned) upsertTimelineSilent({ ...t, sections: named(cleaned) }); return; }
  upsertTimelineSilent({ ...t, sections: named(previous) });
  upsertTimeline({ ...t, sections: named(cleaned) });
}

/** Where a new section's content comes from: an old section, and how many bars into it the new one starts (a split's
 *  second half starts part-way in). */
interface SectionSource { from: string; skip: number }

/** Timeline beats per bar for song clips: OPBridge's engine counts four (opbridge-timelines lib.rs). */
const CLIP_BEATS_PER_BAR = 4;

/**
 * Re-place a timeline's steps and song clips for a new section list (2026-10-16). Steps sit on bars and song clips on
 * beats, both from the timeline's start; sections are contiguous lengths laid over them. So every edit that changes
 * where a section starts must carry what is in it, or the lights, presses and music of every later section end up
 * under the wrong section (the reliability audit: "One bar longer" put every later scene a bar off, and removing,
 * duplicating or moving a section moved its steps but left its song clips behind).
 *
 * Each new section takes, from its source, the bars `skip .. skip + its length`; anything an old section held past
 * what its new self takes is dropped (counted; one undo step brings it back). A section taken twice (a duplicate)
 * gives the second copy new ids. Content after the last section keeps its distance from the end.
 */
function relayout(t: Timeline, next: TimelineSection[], sources: ReadonlyMap<string, SectionSource | null>, nextLeadIn = timelineLeadIn(t)): { lanes: TimelineLane[]; testTrack: Timeline['testTrack']; dropped: number } {
  // Sections start after the lead-in (`firstSectionBar`); what sits in the lead-in stays where it is.
  const startsOf = (list: readonly TimelineSection[], lead: number) => { const m = new Map<string, number>(); let at = lead; for (const x of list) { m.set(x.id, at); at += x.lengthBars; } return { m, total: at }; };
  const before = startsOf(t.sections, timelineLeadIn(t));
  const after = startsOf(next, nextLeadIn);
  const oldAt = (pos: number) => t.sections.find((x) => pos >= before.m.get(x.id)! - 1e-9 && pos < before.m.get(x.id)! + x.lengthBars - 1e-9);
  /** Every place the bar `into` of old section `from` lands in the new list, in order, with the bars left in the new
   *  section from there (`room`). */
  const landings = (from: string, into: number): { pos: number; room: number }[] => {
    const out: { pos: number; room: number }[] = [];
    for (const n of next) {
      const src = sources.get(n.id);
      if (!src || src.from !== from) continue;
      if (into >= src.skip - 1e-9 && into < src.skip + n.lengthBars - 1e-9) out.push({ pos: after.m.get(n.id)! + (into - src.skip), room: src.skip + n.lengthBars - into });
    }
    return out;
  };
  /** A block (anything with `durationBars`: a pattern, a drum block, an animation, a held press) that FITTED its old
   *  section is cut to fit where it lands. Left full length, "One bar shorter" on a section filled by an 8-bar pattern
   *  left it covering bar 1 of the next section, whose own keyframes and Hand backs a block's span silently overrides,
   *  so a master could hold every light for the rest of the song (2026-11-03 audit C-4). A block that already ran
   *  past its section's end did so on purpose (a hold into the chorus) and is left as it is. Counted with the dropped. */
  const fitted = (st: TimelineStep, oldRoom: number, room: number): TimelineStep => {
    const d = (st as { durationBars?: number }).durationBars;
    if (typeof d !== 'number' || d <= room + 1e-9 || d > oldRoom + 1e-9) return st;
    dropped++;
    return { ...st, durationBars: room } as TimelineStep;
  };
  let dropped = 0;
  const lanes = t.lanes.map((lane) => ({
    ...lane,
    steps: lane.steps.flatMap((st) => {
      if (st.position >= before.total - 1e-9) return [{ ...st, position: after.total + (st.position - before.total) }];
      const sec = oldAt(st.position);
      if (!sec) return [st];
      const into = st.position - before.m.get(sec.id)!;
      const lands = landings(sec.id, into);
      if (lands.length === 0) { dropped++; return []; }
      const oldRoom = sec.lengthBars - into;
      return lands.map(({ pos, room }, i) => fitted(i === 0 ? { ...st, position: pos } : { ...(structuredClone(st) as TimelineStep), id: ulid(), position: pos }, oldRoom, room));
    }),
  }));
  let testTrack = t.testTrack;
  const clips = t.testTrack?.clips;
  if (testTrack && clips && clips.length > 0) {
    const B = CLIP_BEATS_PER_BAR;
    const placed: TimelineSongClip[] = [];
    for (const c of clips) {
      const end = c.atBeat + c.beats;
      // The part after the last section keeps its distance from the end.
      if (c.atBeat >= before.total * B - 1e-9) { placed.push({ ...c, atBeat: after.total * B + (c.atBeat - before.total * B) }); continue; }
      let first = true;
      for (const sec of t.sections) {
        const s0 = before.m.get(sec.id)! * B; const s1 = s0 + sec.lengthBars * B;
        const a = Math.max(c.atBeat, s0); const b = Math.min(end, s1);
        if (b <= a + 1e-9) continue;
        for (const n of next) {
          const src = sources.get(n.id);
          if (!src || src.from !== sec.id) continue;
          const t0 = s0 + src.skip * B; const t1 = t0 + n.lengthBars * B;
          const x = Math.max(a, t0); const y = Math.min(b, t1);
          if (y <= x + 1e-9) continue;
          placed.push({ ...c, id: first ? c.id : ulid(), atBeat: after.m.get(n.id)! * B + (x - t0), fromBeat: c.fromBeat + (x - c.atBeat), beats: y - x });
          first = false;
        }
      }
      if (end > before.total * B + 1e-9) {
        const x = Math.max(c.atBeat, before.total * B);
        placed.push({ ...c, id: first ? c.id : ulid(), atBeat: after.total * B + (x - before.total * B), fromBeat: c.fromBeat + (x - c.atBeat), beats: end - x });
      }
    }
    // Pieces of one clip that still run on in both the timeline and the song join up again.
    placed.sort((p, q) => p.atBeat - q.atBeat);
    const joined: TimelineSongClip[] = [];
    for (const c of placed) {
      const prev = joined[joined.length - 1];
      if (prev && Math.abs(prev.atBeat + prev.beats - c.atBeat) < 1e-6 && Math.abs(prev.fromBeat + prev.beats - c.fromBeat) < 1e-6) {
        joined[joined.length - 1] = { ...prev, beats: prev.beats + c.beats };
      } else joined.push(c);
    }
    testTrack = { ...testTrack, clips: joined };
  }
  return { lanes, testTrack, dropped };
}

const identity = (list: readonly TimelineSection[]) => new Map(list.map((x) => [x.id, { from: x.id, skip: 0 } as SectionSource | null]));

/** Say how much a section edit removed (it is one undo step, so Undo brings it back). */
function sayDropped(dropped: number, what: string): void {
  if (dropped > 0) pushToast('warn', `${what} removed ${dropped} change${dropped === 1 ? '' : 's'} that no longer fit. Undo brings ${dropped === 1 ? 'it' : 'them'} back.`, 4000);
}

/** Set a section's length in bars (at least 1), carrying what comes after it (2026-10-16: later sections moved but
 *  their steps and song clips stayed put, so every later scene was a bar off). Shortening removes what was past the
 *  new end, said and undoable. One undo step. */
export function setSectionLength(timelineId: string, sectionId: string, lengthBars: number): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  if (refuseFollowing(t)) return;
  const clamped = Math.max(1, Math.floor(lengthBars));
  const sec = t.sections.find((x) => x.id === sectionId);
  if (!sec || sec.lengthBars === clamped) return;
  const next = t.sections.map((x) => (x.id === sectionId ? { ...x, lengthBars: clamped } : x));
  const r = relayout(t, next, identity(t.sections));
  upsertTimeline({ ...t, sections: next, lanes: r.lanes, ...(r.testTrack ? { testTrack: r.testTrack } : {}) });
  sayDropped(r.dropped, `Shortening ${sec.name}`);
}

/** Set the timeline's whole length in bars (operator, 2026-10-14: "i cant seem to change the length of the
 *  timeline?"): the LAST section grows or shrinks by the difference, keeping at least one bar. One undo step. */
export function setTimelineLength(timelineId: string, totalBars: number): void {
  const t = getTimeline(timelineId);
  if (!t || t.sections.length === 0) return;
  if (refuseFollowing(t)) return;
  const last = t.sections[t.sections.length - 1]!;
  const before = timelineLeadIn(t) + t.sections.reduce((n, s) => n + s.lengthBars, 0) - last.lengthBars;
  const len = Math.max(1, Math.min(maxTimelineLength(t), Math.round(totalBars)) - before);
  if (len === last.lengthBars) return;
  upsertTimeline({ ...t, sections: t.sections.map((s) => (s.id === last.id ? { ...s, lengthBars: len } : s)) });
}

/** A song just linked to a timeline shorter than it (operator, 2026-10-21: "if the timeline is shorter than the song,
 *  expand the number of bars to fit the song in"): the timeline grows to `songBars` (song bar 1 sits on the timeline's
 *  first bar), the last section taking the bars as `setTimelineLength` does. Never shortens. Returns the new length,
 *  or null when nothing changed. */
export function fitTimelineToSong(timelineId: string, songBars: number): number | null {
  const t = getTimeline(timelineId);
  if (!t || t.sections.length === 0) return null;
  if (followsSong(t)) return null; // its length is the song's already
  const total = timelineLeadIn(t) + t.sections.reduce((n, s) => n + s.lengthBars, 0);
  const want = Math.min(maxTimelineLength(t), Math.round(songBars));
  if (want <= total) return null;
  setTimelineLength(timelineId, want);
  return want;
}

/** The shortest `setTimelineLength` can make a timeline: every section but the last kept, the last at one bar
 *  (operator, 2026-10-15: a length that cannot fit is greyed out, never reached by trimming sections). */
export function minTimelineLength(t: Timeline): number {
  return Math.max(1, timelineLeadIn(t) + t.sections.reduce((n, s) => n + s.lengthBars, 0) - (t.sections.at(-1)?.lengthBars ?? 0) + 1);
}

/** The longest: the tier's cap (Genre is uncapped, `timelineOverCap`). */
export function maxTimelineLength(t: Timeline): number {
  return t.tier === 'genre' ? Number.MAX_SAFE_INTEGER : TIMELINE_MAX_BARS_SONG_ACTION;
}

/** "Loop sections" (operator, 2026-10-13: a toggle per timeline): every section loops until you move on, or every
 *  section runs into the next. One undo step. A section set to 'stop' is left alone when turning looping off. */
export function setAllSectionsOnEnd(timelineId: string, onEnd: 'loop' | 'auto-advance'): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  const next = t.sections.map((s) => (onEnd === 'auto-advance' && s.onEnd === 'stop' ? s : { ...s, onEnd }));
  if (next.every((s, i) => s.onEnd === t.sections[i]!.onEnd)) return;
  upsertTimeline({ ...t, sections: next });
}

/** Split a section `atBar` bars into it (1 .. length-1): two sections back to back, the second with the same name
 *  and end rule (a name picks the song's music, so a split chorus is two halves of the chorus). Steps sit on bars,
 *  not in sections, so nothing else moves. One undo step. Returns the new second section. */
export function splitSection(timelineId: string, sectionId: string, atBar: number): TimelineSection | null {
  const t = getTimeline(timelineId);
  if (!t) return null;
  if (refuseFollowing(t)) return null;
  const ix = t.sections.findIndex((s) => s.id === sectionId);
  const s = t.sections[ix];
  const at = Math.round(atBar);
  if (!s || at < 1 || at >= s.lengthBars) return null;
  const second: TimelineSection = { ...s, id: ulid(), lengthBars: s.lengthBars - at };
  const next = [...t.sections.slice(0, ix), { ...s, lengthBars: at }, second, ...t.sections.slice(ix + 1)];
  upsertTimeline({ ...t, sections: next });
  return second;
}

/** Name a section outright (a menu pick, not typing). One undo step. */
export function setSectionName(timelineId: string, sectionId: string, name: string): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  if (refuseFollowing(t)) return;
  const clean = name.trim().toUpperCase();
  if (!clean || !t.sections.some((s) => s.id === sectionId && s.name !== clean)) return;
  upsertTimeline({ ...t, sections: t.sections.map((s) => (s.id === sectionId ? { ...s, name: clean } : s)) });
}

/** Drag a section's right edge to `endBar` (timeline bars from 0): the line between it and the next section moves,
 *  so one grows and the next shrinks by the same amount and nothing else moves (operator, 2026-10-13: "correct").
 *  The last section's edge changes the timeline's length. Each side keeps at least one bar. One undo step. */
export function moveSectionEdge(timelineId: string, sectionId: string, endBar: number): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  if (refuseFollowing(t)) return;
  const ix = t.sections.findIndex((s) => s.id === sectionId);
  if (ix === -1) return;
  const start = timelineLeadIn(t) + t.sections.slice(0, ix).reduce((n, s) => n + s.lengthBars, 0);
  const s = t.sections[ix]!;
  const nextS = t.sections[ix + 1];
  const maxEnd = nextS ? start + s.lengthBars + nextS.lengthBars - 1 : Number.POSITIVE_INFINITY;
  const end = Math.max(start + 1, Math.min(maxEnd, Math.round(endBar)));
  const len = end - start;
  if (len === s.lengthBars) return;
  const delta = len - s.lengthBars;
  const next = t.sections.map((x, i) => (i === ix ? { ...x, lengthBars: len } : nextS && i === ix + 1 ? { ...x, lengthBars: x.lengthBars - delta } : x));
  upsertTimeline({ ...t, sections: next });
}

/** Drag the FIRST section's left edge to `bar` (2026-10-19, operator: "i need to be able to move the left side of the
 *  intro section. the intro section can start a few bars after the start of the timeline if need be"): the bars before
 *  it become a lead-in where the song plays and nothing else does (`firstSectionBar`). The first section's END stays
 *  put, so it grows or shrinks by the opposite amount, keeping at least one bar; nothing else moves (steps and song
 *  clips stay where they are in time, as `moveSectionEdge`). Back to 0 removes the lead-in. One undo step. */
export function setFirstSectionBar(timelineId: string, bar: number): void {
  const t = getTimeline(timelineId);
  const first = t?.sections[0];
  if (!t || !first) return;
  if (refuseFollowing(t)) return;
  const lead = timelineLeadIn(t);
  const end = lead + first.lengthBars;
  const at = Math.max(0, Math.min(end - 1, Math.round(bar)));
  if (at === lead) return;
  const { firstSectionBar: _old, ...rest } = t;
  upsertTimeline({ ...rest, ...(at > 0 ? { firstSectionBar: at } : {}), sections: t.sections.map((s, i) => (i === 0 ? { ...s, lengthBars: end - at } : s)) });
}

/** Section names compare loosely, as the song's marks do ("PRE-CHORUS" = "Pre chorus"), numbers kept (VERSE 2 is
 *  not VERSE). */
const sameName = (a: string, b: string) => a.toLowerCase().replace(/[^a-z0-9]/g, '') === b.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Does this timeline's section structure follow its song's parts (`testTrack.partsFollow`, not while clips exist)? */
export function followsSong(t: Timeline | undefined): boolean {
  return !!t?.testTrack?.partsFollow && !t.testTrack.clips;
}

/** A structural section edit on a timeline whose sections are its song's parts: refused, and said where it is done. */
function refuseFollowing(t: Timeline): boolean {
  if (!followsSong(t)) return false;
  pushToast('warn', "This timeline's sections are its song's parts: change them on the song row.", 3500);
  return true;
}

/**
 * Lay a timeline's sections onto its song's parts (2026-10-21, `songParts.ts::sectionPlan`): the lead-in, then one
 * section per part, a timeline bar being the song's bar. Replaces "Make sections from the song" (2026-10-14).
 *
 * FIRST time (the timeline's sections were its own): its steps move with their sections, as before: a section named
 * like a part, else the same kind in the same order (VERSE -> VERSE 1), keeps its id (launchers), end rule and steps;
 * steps of a section the song lacks, or past a part's end, go (`dropped`, one undo step brings them back).
 * After that (`partsFollow`) the sections are relabelled to the song's parts with NOTHING moved: the lights are on the
 * song's bars, and re-cutting the song is a new label, not new music. That relabel is not an undo step of its own
 * (the song's edit is), so one Undo takes back a part and its sections together. `record: false` starts a timeline
 * with no steps silently (nothing moves, and an undo step would only start it again).
 */
export function followSongParts(timelineId: string, plan: { leadIn: number; sections: ReadonlyArray<{ name: string; lengthBars: number }> }, opts: { record?: boolean } = {}): { dropped: number; first: boolean } | null {
  const t = getTimeline(timelineId);
  if (!t?.testTrack || t.testTrack.clips || plan.sections.length === 0) return null;
  const first = !t.testTrack.partsFollow;
  const lead = timelineLeadIn(t);
  const oldStart = new Map<string, number>();
  { let at = lead; for (const s of t.sections) { oldStart.set(s.id, at); at += s.lengthBars; } }
  const used = new Set<string>();
  const fresh = (name: string): TimelineSection => ({ id: ulid(), name, lengthBars: 1, ...(name ? { onEnd: t.tier === 'action' ? 'stop' : 'loop' } : { onEnd: 'auto-advance' }) });
  let at = Math.max(0, Math.round(plan.leadIn));
  const sections: TimelineSection[] = plan.sections.map((p) => {
    const start = at; at += p.lengthBars;
    const kind = p.name ? kindOf(p.name) : '';
    const sameKind = (s: TimelineSection) => !used.has(s.id) && (s.name ? kindOf(s.name) : '') === kind;
    let old: TimelineSection | undefined;
    if (first) {
      old = t.sections.find((s) => !used.has(s.id) && sameName(s.name, p.name)) ?? (p.name ? t.sections.find(sameKind) : undefined);
    } else {
      // The section of the same kind this part overlaps most (so VERSE -> VERSE 1 keeps its id and end rule).
      let best = 0;
      for (const s of t.sections) {
        if (!sameKind(s)) continue;
        const a = oldStart.get(s.id)!; const o = Math.min(a + s.lengthBars, start + p.lengthBars) - Math.max(a, start);
        if (o > best) { best = o; old = s; }
      }
    }
    if (old) used.add(old.id);
    return { ...(old ?? fresh(p.name)), name: p.name.trim().toUpperCase(), lengthBars: Math.max(1, Math.round(p.lengthBars)) };
  });
  const leadIn = Math.max(0, Math.round(plan.leadIn));
  const same = !first && leadIn === lead && sections.length === t.sections.length
    && sections.every((s, i) => s.id === t.sections[i]!.id && s.name === t.sections[i]!.name && s.lengthBars === t.sections[i]!.lengthBars);
  if (same) return { dropped: 0, first };
  const { firstSectionBar: _fb, ...rest } = t;
  const base: Timeline = { ...rest, ...(leadIn > 0 ? { firstSectionBar: leadIn } : {}), sections, testTrack: { ...t.testTrack, partsFollow: true } };
  if (!first || opts.record === false) { upsertTimelineSilent(base); return { dropped: 0, first }; }
  const sources = new Map<string, SectionSource | null>(sections.map((s) => [s.id, used.has(s.id) ? { from: s.id, skip: 0 } : null]));
  const r = relayout(t, sections, sources, leadIn);
  upsertTimeline({ ...base, lanes: r.lanes });
  return { dropped: r.dropped, first };
}

/**
 * "Copy lights from" (2026-10-14): section `toId` takes a copy of every lane's steps in section `fromId` (lights,
 * cues, controls), at the same place in the bar, replacing what it had. A longer source is cut at the target's end;
 * a shorter one leaves the rest empty. One undo step. Returns how many steps were copied.
 */
export function copySectionSteps(timelineId: string, fromId: string, toId: string): number {
  const t = getTimeline(timelineId);
  if (!t || fromId === toId) return 0;
  let at = timelineLeadIn(t);
  const start = new Map<string, number>();
  for (const s of t.sections) { start.set(s.id, at); at += s.lengthBars; }
  const from = t.sections.find((s) => s.id === fromId);
  const to = t.sections.find((s) => s.id === toId);
  if (!from || !to) return 0;
  const fs = start.get(fromId)!; const ts = start.get(toId)!;
  const span = Math.min(from.lengthBars, to.lengthBars);
  let copied = 0;
  const lanes = t.lanes.map((lane) => {
    const keep = lane.steps.filter((st) => !(st.position >= ts && st.position < ts + to.lengthBars));
    const copies = lane.steps
      .filter((st) => st.position >= fs && st.position < fs + span)
      .map((st) => ({ ...(structuredClone(st) as TimelineStep), id: ulid(), position: ts + (st.position - fs) }));
    copied += copies.length;
    return { ...lane, steps: [...keep, ...copies] };
  });
  upsertTimeline({ ...t, lanes });
  return copied;
}

/**
 * Remove a section AND what is in it (2026-10-16): its steps and song clips go with it and everything after moves back
 * to close the gap (it used to remove only the section, so the next section played the removed one's lights and every
 * later section was off by its length). No-op if it's the last one (a timeline must have >=1). One undo step.
 */
export function removeSection(timelineId: string, sectionId: string): void {
  const t = getTimeline(timelineId);
  if (!t || t.sections.length <= 1) return;
  if (refuseFollowing(t)) return;
  if (!t.sections.some((s) => s.id === sectionId)) return;
  const next = t.sections.filter((s) => s.id !== sectionId);
  const r = relayout(t, next, identity(next));
  upsertTimeline({ ...t, sections: next, lanes: r.lanes, ...(r.testTrack ? { testTrack: r.testTrack } : {}) });
}

/**
 * Duplicate a section with what is in it (2026-10-16, "sections can't be duplicated" - lanes could): the copy goes
 * right after it, same name, length and end rule, its steps (new ids) and song clips copied at the same place in the
 * bar; everything after moves later by its length. Refused (null) past the timeline's longest. One undo step.
 */
export function duplicateSection(timelineId: string, sectionId: string): TimelineSection | null {
  const t = getTimeline(timelineId);
  if (!t) return null;
  if (refuseFollowing(t)) return null;
  const ix = t.sections.findIndex((s) => s.id === sectionId);
  const sec = t.sections[ix];
  if (!sec) return null;
  const total = timelineLeadIn(t) + t.sections.reduce((n, s) => n + s.lengthBars, 0);
  if (total + sec.lengthBars > maxTimelineLength(t)) return null;
  const copy: TimelineSection = { ...structuredClone(sec), id: ulid() };
  const next = [...t.sections.slice(0, ix + 1), copy, ...t.sections.slice(ix + 1)];
  const sources = identity(t.sections);
  sources.set(copy.id, { from: sec.id, skip: 0 });
  const r = relayout(t, next, sources);
  upsertTimeline({ ...t, sections: next, lanes: r.lanes, ...(r.testTrack ? { testTrack: r.testTrack } : {}) });
  return copy;
}

/**
 * Move a section one place earlier (-1) or later (+1), with what is in it (2026-10-16): it swaps with its neighbour
 * and each takes its own steps and song clips. Section ids are kept, so launchers that start a section still find it.
 * No-op at either end. One undo step.
 */
export function moveSection(timelineId: string, sectionId: string, dir: -1 | 1): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  if (refuseFollowing(t)) return;
  const ix = t.sections.findIndex((s) => s.id === sectionId);
  const jx = ix + dir;
  if (ix === -1 || jx < 0 || jx >= t.sections.length) return;
  const next = [...t.sections];
  next[ix] = t.sections[jx]!;
  next[jx] = t.sections[ix]!;
  const r = relayout(t, next, identity(t.sections));
  upsertTimeline({ ...t, sections: next, lanes: r.lanes, ...(r.testTrack ? { testTrack: r.testTrack } : {}) });
}

/** Rename the whole timeline. Stores the raw value — commit-time cleanup
 *  happens via `commitTimelineName`. */
export function renameTimeline(timelineId: string, name: string): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  upsertTimelineSilent({ ...t, name });
}

/** Commit-time cleanup for a timeline name. Called on blur/Enter. Empty
 *  reverts to `previous` (the value when editing started); if previous
 *  was also empty, uses 'Untitled Timeline'. */
export function commitTimelineName(timelineId: string, previous: string): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  const trimmed = t.name.trim();
  const cleaned = trimmed || previous || 'Untitled Timeline';
  // ONE UNDO STEP PER RENAME (2026-10-16). Typing saves silently, so by
  // now the store already holds the typed name and an early "nothing to
  // tidy" return recorded no step at all: no name edit could be undone
  // (found building the timeline editor's in-place rename). Put the name it
  // had before typing back silently, so the undo-tracked write's checkpoint
  // holds it; a rename that ends where it started records nothing.
  if (cleaned === previous) { if (t.name !== cleaned) upsertTimelineSilent({ ...t, name: cleaned }); return; }
  upsertTimelineSilent({ ...t, name: previous });
  upsertTimeline({ ...t, name: cleaned });
}

/** Toggle / set the whole-timeline loop flag (the editor control next to
 *  Play). Undo-tracked. Undefined stored as `false`. */
/** When the timeline takes a fader back after a hand moved it (`Timeline.touchMode`). */
export function setTimelineTouchMode(timelineId: string, mode: Timeline['touchMode']): void {
  const t = getTimeline(timelineId);
  if (!t || t.touchMode === mode) return;
  const { touchMode: _old, ...rest } = t;
  upsertTimeline(mode ? { ...rest, touchMode: mode } : rest);
}

/** What the lights do when the timeline starts (`Timeline.lightsAtStart`); undefined = the tier's default. Undo-tracked. */
export function setTimelineLightsAtStart(timelineId: string, mode: Timeline['lightsAtStart']): void {
  const t = getTimeline(timelineId);
  if (!t || t.lightsAtStart === mode) return;
  const { lightsAtStart: _old, ...rest } = t;
  upsertTimeline(mode ? { ...rest, lightsAtStart: mode } : rest);
}

/** What the lights do when the timeline ends (`Timeline.lightsAtEnd`); undefined = the tier's default. Undo-tracked. */
export function setTimelineLightsAtEnd(timelineId: string, mode: Timeline['lightsAtEnd']): void {
  const t = getTimeline(timelineId);
  if (!t || t.lightsAtEnd === mode) return;
  const { lightsAtEnd: _old, ...rest } = t;
  upsertTimeline(mode ? { ...rest, lightsAtEnd: mode } : rest);
}

/** The show's opt-in to real strobes (`Timeline.strobes`, DEC-262): a strobe drum look plays only where it is on.
 *  Undo-tracked; off is stored as absent. */
export function setTimelineStrobes(timelineId: string, on: boolean): void {
  const t = getTimeline(timelineId);
  if (!t || (t.strobes === true) === on) return;
  const { strobes: _old, ...rest } = t;
  upsertTimeline(on ? { ...rest, strobes: true } : rest);
}

export function setTimelineLoop(timelineId: string, loop: boolean): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  if ((t.loop ?? false) === loop) return;
  upsertTimeline({ ...t, loop });
}

/** Point a timeline at a test track (OPBridge's), or `null` to drop it. Where it plays (`pc`, `output`) is kept
 *  across a change of song. One undo step. */
/** Mute or solo the timeline's song as a lane (2026-10-16): on, or off when it already is. One undo step. */
export function toggleSongFlag(timelineId: string, key: 'mute' | 'solo'): void {
  const t = getTimeline(timelineId);
  if (!t?.testTrack) return;
  const { [key]: was, ...rest } = t.testTrack;
  upsertTimeline({ ...t, testTrack: was ? rest : { ...rest, [key]: true } });
}

/** Returns whether the timeline now has a different song than before (a rename alone is not one). */
export function setTimelineTestTrack(timelineId: string, track: { id: string; name: string } | null): boolean {
  const t = getTimeline(timelineId);
  if (!t) return false;
  const changed = (t.testTrack?.id ?? null) !== (track?.id ?? null);
  if (!changed && (t.testTrack?.name ?? null) === (track?.name ?? null)) return false;
  const { testTrack: old, ...rest } = t;
  upsertTimeline(track ? { ...rest, testTrack: { ...(old?.pc ? { pc: old.pc } : {}), ...(old?.output !== undefined ? { output: old.output } : {}), ...(old?.skipIntro ? { skipIntro: true } : {}), id: track.id, name: track.name } } : rest);
  return changed;
}

/** Whether the timeline's song skips its intro (the audio before bar 1). One undo step. */
export function setTimelineSongSkipIntro(timelineId: string, skip: boolean): void {
  const t = getTimeline(timelineId);
  if (!t?.testTrack || !!t.testTrack.skipIntro === skip) return;
  const { skipIntro: _old, ...tt } = t.testTrack;
  upsertTimeline({ ...t, testTrack: skip ? { ...tt, skipIntro: true } : tt });
}

/** The timeline's song clips (2026-10-13, trim and split); null goes back to the song's section marks. One undo step. */
export function setTimelineSongClips(timelineId: string, clips: TimelineSongClip[] | null, grid?: { tempoChoice: 'as-read' | 'half' | 'double'; bar1Sec: number }): void {
  const t = getTimeline(timelineId);
  if (!t?.testTrack) return;
  const { clips: _old, clipsGrid: _g, ...tt } = t.testTrack;
  upsertTimeline({ ...t, testTrack: clips ? { ...tt, clips, ...(grid ? { clipsGrid: grid } : {}) } : tt });
}

/** Where a timeline's song plays: `pc` (node_id; "" = where the timeline plays) and `output` (device name; "" =
 *  that PC's default). Stored on the timeline so a pad or a Pacer press knows too. One undo step. */
export function setTimelineTestTrackPlace(timelineId: string, place: { pc?: string; output?: string }): void {
  const t = getTimeline(timelineId);
  if (!t?.testTrack) return;
  const next = { ...t.testTrack };
  if (place.pc !== undefined) { if (place.pc) next.pc = place.pc; else delete next.pc; }
  if (place.output !== undefined) { if (place.output) next.output = place.output; else delete next.output; }
  if (next.pc === t.testTrack.pc && next.output === t.testTrack.output) return;
  upsertTimeline({ ...t, testTrack: next });
}

// ─── Lane mutators ───────────────────────────────────────────────

const LANE_KIND_DEFAULT_NAME: Record<TimelineLaneKind, string> = {
  midi: 'MIDI',
  osc: 'OSC',
  marker: 'Markers',
  mixed: 'Lane',
  'dmx-rig': 'DMX Rig',
  'dmx-fixture': 'DMX Fixture',
  guitar: 'Guitar',
  opvisual: 'OPVisual',
  'opvisual-colour': 'OPVisual colour',
  'opvisual-palette': 'OPVisual palette',
  automation: 'Automation',
  instrument: 'Drum lights',
};

/** Append a new lane of the given kind. Auto-numbers when a lane of the
 *  same kind already exists (MIDI, MIDI 2, MIDI 3...). Returns the new lane. */
export function addLane(timelineId: string, laneKind: TimelineLaneKind): TimelineLane | null {
  const t = getTimeline(timelineId);
  if (!t) return null;
  const base = LANE_KIND_DEFAULT_NAME[laneKind];
  const sameKindCount = t.lanes.filter((l) => l.laneKind === laneKind).length;
  const name = sameKindCount === 0 ? base : `${base} ${sameKindCount + 1}`;
  const lane: TimelineLane = { id: ulid(), name, laneKind, steps: [] };
  upsertTimeline({ ...t, lanes: [...t.lanes, lane] });
  return lane;
}

/** Rename a lane. Stores the raw value — commit-time cleanup happens via
 *  `commitLaneName`. */
export function renameLane(timelineId: string, laneId: string, name: string): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  const nextLanes = t.lanes.map((l) =>
    l.id === laneId ? { ...l, name } : l,
  );
  upsertTimelineSilent({ ...t, lanes: nextLanes });
}

/** Commit-time cleanup for a lane name. Empty reverts to `previous`; if
 *  previous was also empty, falls back to the lane's laneKind default
 *  (MIDI / OSC / DMX / Fixture / etc.). Called on blur/Enter. */
export function commitLaneName(timelineId: string, laneId: string, previous: string): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  const ix = t.lanes.findIndex((l) => l.id === laneId);
  if (ix === -1) return;
  const cur = t.lanes[ix]!;
  const trimmed = cur.name.trim();
  const fallback = LANE_KIND_DEFAULT_NAME[cur.laneKind ?? 'mixed'];
  const cleaned = trimmed || previous || fallback;
  const named = (name: string) => t.lanes.map((l, i) => (i === ix ? { ...l, name } : l));
  // ONE UNDO STEP PER RENAME (2026-10-16). Typing saves silently, so by
  // now the store already holds the typed name and an early "nothing to
  // tidy" return recorded no step at all: no name edit could be undone
  // (found building the timeline editor's in-place rename). Put the name it
  // had before typing back silently, so the undo-tracked write's checkpoint
  // holds it; a rename that ends where it started records nothing.
  if (cleaned === previous) { if (cur.name !== cleaned) upsertTimelineSilent({ ...t, lanes: named(cleaned) }); return; }
  upsertTimelineSilent({ ...t, lanes: named(previous) });
  upsertTimeline({ ...t, lanes: named(cleaned) });
}

/** Remove a lane. No-op if it would leave zero lanes. */
export function removeLane(timelineId: string, laneId: string): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  if (t.lanes.length <= 1) return;
  upsertTimeline({ ...t, lanes: t.lanes.filter((l) => l.id !== laneId) });
}

/** Duplicate a lane and its steps (fresh ULIDs throughout) and insert the
 *  copy directly after the original. Every lane kind is flat now (2026-08-04
 *  — no more parent/child fixture-group structure), so there's no longer a
 *  kind this can't safely clone; a `dmx-fixture` lane's `fixtureRef` copies
 *  along with it (two lanes targeting the same fixture is a legitimate
 *  starting point for "same fixture, different cue"). Returns the new lane,
 *  or null if the source is missing. */
export function duplicateLane(timelineId: string, laneId: string): TimelineLane | null {
  const t = getTimeline(timelineId);
  if (!t) return null;
  const ix = t.lanes.findIndex((l) => l.id === laneId);
  if (ix === -1) return null;
  const src = t.lanes[ix]!;
  const copy: TimelineLane = {
    ...src,
    id: ulid(),
    name: `${src.name} copy`,
    steps: src.steps.map((s) => ({ ...s, id: ulid() })),
  };
  const nextLanes = [...t.lanes.slice(0, ix + 1), copy, ...t.lanes.slice(ix + 1)];
  upsertTimeline({ ...t, lanes: nextLanes });
  return copy;
}

// ─── Overwrite (2026-09-29) ──────────────────────────────────────
//
// Operator: "if i drag a block over another block, the block stays
// underneath. i presumed it would overwrite the section of the block that it
// covered." DAW convention (Ableton's arrangement, Logic): the block you drop,
// drag or resize WINS, and whatever part of another block it covers is cut
// away - trimmed, split in two, or removed if fully covered. Before this a
// moved block just overlapped the other one and, being earlier in the lane's
// array, drew underneath it.

const MIN_PIECE_BARS = 1 / 16; // the same floor `setStepDuration` clamps to
const OVERLAP_EPS = 1e-9;

/** Everything drawn as a block. Colour markers are points on a colour lane,
 *  not blocks, and are never cut. */
function isBlockStep(s: TimelineStep): boolean {
  // A Hand back (2026-10-02) is a point too, and so is a drum lights route (2026-10-23): it holds until the next one.
  return s.kind !== 'colorMarker' && s.kind !== 'lightRelease' && s.kind !== 'hitRoute';
}

/** A block's extent in bars, as the editor draws it: an authored
 *  `durationBars` on a durational kind, else one beat. */
function blockExtent(s: TimelineStep, sigNum: number): { start: number; end: number; durational: boolean } {
  const durational = s.kind === 'buttonRef' || s.kind === 'animation' || s.kind === 'pattern' || s.kind === 'hitBlock';
  const authored = durational ? (s as { durationBars?: number }).durationBars : undefined;
  const dur = typeof authored === 'number' && authored > 0 ? authored : 1 / sigNum;
  return { start: s.position, end: s.position + dur, durational };
}

/**
 * Cut away the part of every OTHER block in `steps` that the block `keptId`
 * covers. Pure (exported for smokes). Per other block:
 *  - no overlap: untouched (touching edges do not overlap);
 *  - fully covered: removed;
 *  - covered at its end: trimmed to end where the kept block starts;
 *  - covered at its start: starts where the kept block ends (a held note
 *    re-strikes there, as a trimmed clip does in a DAW);
 *  - kept block inside it: split in two, the tail a new step starting where
 *    the kept block ends.
 * An instant block (one-beat footprint, no `durationBars`) is removed when its
 * trigger point is covered, else left alone. A piece shorter than 1/16 bar is
 * dropped rather than kept as an unclickable sliver.
 */
export function overwriteCovered(steps: TimelineStep[], keptId: string, sigNum: number): TimelineStep[] {
  const kept = steps.find((s) => s.id === keptId);
  if (!kept || !isBlockStep(kept)) return steps;
  const beats = Math.max(1, sigNum);
  const k = blockExtent(kept, beats);
  const out: TimelineStep[] = [];
  for (const s of steps) {
    if (s.id === keptId || !isBlockStep(s)) { out.push(s); continue; }
    const o = blockExtent(s, beats);
    if (o.end <= k.start + OVERLAP_EPS || o.start >= k.end - OVERLAP_EPS) { out.push(s); continue; }
    if (!o.durational) {
      const triggerCovered = o.start >= k.start - OVERLAP_EPS && o.start < k.end - OVERLAP_EPS;
      if (!triggerCovered) out.push(s);
      continue;
    }
    const before = k.start - o.start;
    const after = o.end - k.end;
    const keepBefore = before >= MIN_PIECE_BARS - OVERLAP_EPS;
    const keepAfter = after >= MIN_PIECE_BARS - OVERLAP_EPS;
    if (keepBefore) out.push({ ...s, durationBars: before } as TimelineStep);
    if (keepAfter) {
      out.push(keepBefore
        ? ({ ...s, id: ulid(), position: k.end, durationBars: after } as TimelineStep)
        : ({ ...s, position: k.end, durationBars: after } as TimelineStep));
    }
  }
  return out;
}

/** Where a step's drawn block ends, in bars (its start plus its authored
 *  length, or one beat). Exported so Duplicate can place copies AFTER the
 *  selection's end rather than one beat after its last start. */
export function stepEndBars(s: TimelineStep, sigNum: number): number {
  return blockExtent(s, Math.max(1, sigNum)).end;
}

/**
 * Add several new steps to one lane's existing steps, each new block cutting
 * what it covers of the EXISTING ones (paste and Ctrl+D, operator 2026-10-01:
 * "same as a drag"). Pure (exported for smokes).
 *
 * New blocks never cut each other: a pasted group keeps the layout it was
 * copied with. A new colour marker replaces an existing marker at the same
 * beat (markers are points, never cut by blocks, and two at one beat would
 * leave which colour plays to array order).
 */
export function overwriteWithMany(existing: TimelineStep[], added: TimelineStep[], sigNum: number): TimelineStep[] {
  let pieces = existing;
  for (const nb of added) {
    if (nb.kind === 'colorMarker') {
      pieces = pieces.filter((s) => !(s.kind === 'colorMarker' && Math.abs(s.position - nb.position) < OVERLAP_EPS));
      continue;
    }
    pieces = overwriteCovered([...pieces, nb], nb.id, sigNum).filter((s) => s.id !== nb.id);
  }
  return [...pieces, ...added];
}

/** Add steps across lanes in ONE edit (one undo step), each cutting what it
 *  covers of the steps already there - `overwriteWithMany`. Used by paste and
 *  Duplicate (Ctrl+D). */
export function placeStepsBulk(
  timelineId: string,
  additions: Array<{ laneId: string; step: TimelineStep }>,
): void {
  if (additions.length === 0) return;
  const t = getTimeline(timelineId);
  if (!t) return;
  const byLane = new Map<string, TimelineStep[]>();
  for (const a of additions) {
    let arr = byLane.get(a.laneId);
    if (!arr) { arr = []; byLane.set(a.laneId, arr); }
    arr.push(a.step);
  }
  const nextLanes = t.lanes.map((l) => {
    const adds = byLane.get(l.id);
    if (!adds || adds.length === 0) return l;
    return { ...l, steps: overwriteWithMany(l.steps, adds, signatureBeats(t)) };
  });
  upsertTimeline({ ...t, lanes: nextLanes });
}

function signatureBeats(t: Timeline): number {
  return Math.max(1, t.signature?.num ?? 4);
}

// ─── Step mutators ───────────────────────────────────────────────

/** Insert a step into a lane at the given fractional-bar position. Returns
 *  the created step id (or null if the lane isn't found). `overwrite` cuts
 *  away whatever the new block covers (a palette drop); off by default so a
 *  duplicate-in-place (Alt+drag) or a paste is not affected. */
export function addStep(
  timelineId: string,
  laneId: string,
  step: TimelineStep,
  opts: { overwrite?: boolean } = {},
): string | null {
  const t = getTimeline(timelineId);
  if (!t) return null;
  const laneIx = t.lanes.findIndex((l) => l.id === laneId);
  if (laneIx === -1) return null;
  const nextLanes = t.lanes.map((l, i) => {
    if (i !== laneIx) return l;
    const steps = [...l.steps, step];
    return { ...l, steps: opts.overwrite ? overwriteCovered(steps, step.id, signatureBeats(t)) : steps };
  });
  upsertTimeline({ ...t, lanes: nextLanes });
  return step.id;
}

/** Move and/or resize one block in ONE edit (one undo entry), then cut away
 *  what it covers. Used by the editor's block drag and both resize handles.
 *  Position clamps to >= 0; `durationBars` applies to durational kinds only
 *  and clamps to >= 1/16 bar, as `setStepDuration` does. Replaces the old
 *  left-resize path of `moveStep` then `setStepDuration`, whose in-between
 *  state (new start, OLD length) would have cut blocks the final one does
 *  not cover. */
export function placeStep(
  timelineId: string,
  laneId: string,
  stepId: string,
  change: { position?: number; durationBars?: number },
): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  const nextLanes = t.lanes.map((l) => {
    if (l.id !== laneId) return l;
    const steps = l.steps.map((s) => {
      if (s.id !== stepId) return s;
      let next: TimelineStep = s;
      if (change.position !== undefined) next = { ...next, position: Math.max(0, change.position) };
      if (change.durationBars !== undefined && (s.kind === 'animation' || s.kind === 'buttonRef' || s.kind === 'pattern' || s.kind === 'hitBlock')) {
        next = { ...next, durationBars: Math.max(1 / 16, change.durationBars) } as TimelineStep;
      }
      return next;
    });
    return { ...l, steps: overwriteCovered(steps, stepId, signatureBeats(t)) };
  });
  upsertTimeline({ ...t, lanes: nextLanes });
}

/** Clone an existing step in place — new id, same everything else. Used
 *  by Alt+drag: duplicate the source, then drag the clone. Returns the
 *  new step id, or null if source not found. */
export function duplicateStep(timelineId: string, laneId: string, stepId: string): string | null {
  const t = getTimeline(timelineId);
  if (!t) return null;
  const lane = t.lanes.find((l) => l.id === laneId);
  if (!lane) return null;
  const src = lane.steps.find((s) => s.id === stepId);
  if (!src) return null;
  const clone: TimelineStep = { ...src, id: ulid() } as TimelineStep;
  return addStep(timelineId, laneId, clone);
}

export function removeStep(timelineId: string, laneId: string, stepId: string): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  const nextLanes = t.lanes.map((l) =>
    l.id === laneId ? { ...l, steps: l.steps.filter((s) => s.id !== stepId) } : l,
  );
  upsertTimeline({ ...t, lanes: nextLanes });
}

/** Move an existing step to a new fractional-bar position (used by drag).
 *  Clamps to >= 0. */
export function moveStep(
  timelineId: string,
  laneId: string,
  stepId: string,
  position: number,
  /** When set, and another step exists at the target position, reorder
   *  the moved step relative to the occupant so it sits on the side the
   *  drag approached from. `'left'` = put moved step BEFORE occupant;
   *  `'right'` = AFTER. Undefined = don't reorder (legacy behaviour).
   *
   *  Storage: consecutive steps with equal `position` are legal; sort
   *  order at render/sample time is by (position, arrayIndex), so
   *  array order breaks the tie deterministically. */
  resolveDirection?: 'left' | 'right',
): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  const clamped = Math.max(0, position);
  const nextLanes = t.lanes.map((l) => {
    if (l.id !== laneId) return l;
    // Update the step's position first.
    const withNewPos = l.steps.map((s) =>
      s.id === stepId ? { ...s, position: clamped } : s,
    );
    if (!resolveDirection) {
      return { ...l, steps: withNewPos };
    }
    // Check for an occupant at the same position (not the moved step).
    const movedIx = withNewPos.findIndex((s) => s.id === stepId);
    if (movedIx === -1) return { ...l, steps: withNewPos };
    const moved = withNewPos[movedIx]!;
    // Find neighbours at the same position (excluding the moved step).
    const occupants: number[] = [];
    for (let i = 0; i < withNewPos.length; i++) {
      if (i === movedIx) continue;
      if (withNewPos[i]!.position === clamped) occupants.push(i);
    }
    if (occupants.length === 0) return { ...l, steps: withNewPos };
    // Reorder: remove moved, then splice in the requested side of the
    // first occupant. First occupant is enough because ties among the
    // remaining steps preserve their relative order — pinning the moved
    // step on one side of the block puts it at that visual position.
    const withoutMoved = withNewPos.filter((_, i) => i !== movedIx);
    // The occupant index shifts by -1 if it was after movedIx in the
    // original array — recompute against the filtered array using id.
    const firstOccupantId = withNewPos[occupants[0]!]!.id;
    const targetIx = withoutMoved.findIndex((s) => s.id === firstOccupantId);
    if (targetIx === -1) return { ...l, steps: withNewPos };
    const insertIx = resolveDirection === 'left' ? targetIx : targetIx + 1;
    const reordered = [
      ...withoutMoved.slice(0, insertIx),
      moved,
      ...withoutMoved.slice(insertIx),
    ];
    return { ...l, steps: reordered };
  });
  upsertTimeline({ ...t, lanes: nextLanes });
}

/** Atomic cross-lane move — remove from source lane, insert into dest
 *  lane at `position`. No-op if either lane is missing or the step isn't
 *  in the source lane. */
export function moveStepToLane(
  timelineId: string,
  fromLaneId: string,
  toLaneId: string,
  stepId: string,
  position: number,
): void {
  if (fromLaneId === toLaneId) {
    moveStep(timelineId, fromLaneId, stepId, position);
    return;
  }
  const t = getTimeline(timelineId);
  if (!t) return;
  const src = t.lanes.find((l) => l.id === fromLaneId);
  const dst = t.lanes.find((l) => l.id === toLaneId);
  if (!src || !dst) return;
  const moving = src.steps.find((s) => s.id === stepId);
  if (!moving) return;
  const clamped = Math.max(0, position);
  const moved: TimelineStep = { ...moving, position: clamped };
  const nextLanes = t.lanes.map((l) => {
    if (l.id === fromLaneId) return { ...l, steps: l.steps.filter((s) => s.id !== stepId) };
    // The moved block wins on its new lane (2026-09-29 overwrite rule).
    if (l.id === toLaneId) return { ...l, steps: overwriteCovered([...l.steps, moved], stepId, signatureBeats(t)) };
    return l;
  });
  upsertTimeline({ ...t, lanes: nextLanes });
}

/** Set a duration-bearing step's `durationBars`. Clamps to >= a minimum
 *  (1/16 bar so the step is visible + selectable). Applies to animation
 *  and buttonRef steps; no-op on other kinds. */
export function setStepDuration(
  timelineId: string,
  laneId: string,
  stepId: string,
  durationBars: number,
): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  const clamped = Math.max(1 / 16, durationBars);
  const nextLanes = t.lanes.map((l) =>
    l.id === laneId
      ? {
          ...l,
          steps: l.steps.map((s) => {
            if (s.id !== stepId) return s;
            if (s.kind !== 'animation' && s.kind !== 'buttonRef' && s.kind !== 'pattern' && s.kind !== 'hitBlock') return s;
            return { ...s, durationBars: clamped };
          }),
        }
      : l,
  );
  upsertTimeline({ ...t, lanes: nextLanes });
}

/** Replace one step with an edited copy (same id), in ONE edit: the drum lights panel's every change (2026-10-23).
 *  A block whose span changed cuts what it now covers, as a drag does. */
export function replaceStep(timelineId: string, laneId: string, step: TimelineStep): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  let found = false;
  const nextLanes = t.lanes.map((l) => {
    if (l.id !== laneId) return l;
    const steps = l.steps.map((s) => {
      if (s.id !== step.id) return s;
      found = true;
      return step;
    });
    return { ...l, steps: overwriteCovered(steps, step.id, signatureBeats(t)) };
  });
  if (found) upsertTimeline({ ...t, lanes: nextLanes });
}

/** Add whole lanes at once, ONE undo step (a drum lights preset can bring two: "Kick + snare chase"). */
export function addLanes(timelineId: string, lanes: TimelineLane[]): void {
  const t = getTimeline(timelineId);
  if (!t || lanes.length === 0) return;
  upsertTimeline({ ...t, lanes: [...t.lanes, ...lanes] });
}

/** Convenience: build a new ButtonRefStep placed at `position`. */
export function makeButtonRefStep(buttonId: string, position: number, labelHint?: string): ButtonRefStep {
  const step: ButtonRefStep = { id: ulid(), kind: 'buttonRef', position, buttonId };
  if (labelHint !== undefined) step.labelHint = labelHint;
  return step;
}

/** Convenience: build a new TargetValueStep placed at `position` - the
 *  Milestone 1 Rig State slice (2026-08-15). `value` is whatever the
 *  live-state drawer captured (or, later, an explicitly-picked value). */
export function makeTargetValueStep(
  targetId: string,
  value: number | string,
  position: number,
  labelHint?: string,
): TargetValueStep {
  const step: TargetValueStep = { id: ulid(), kind: 'targetValue', position, targetId, value };
  if (labelHint !== undefined) step.labelHint = labelHint;
  return step;
}

/** First lane whose `laneKind` matches. Undefined if none. Used by drop-
 *  auto-route (a button of MIDI kind lands on the first MIDI lane). */
export function findFirstLaneOfKind(timeline: Timeline, laneKind: TimelineLaneKind): TimelineLane | undefined {
  return timeline.lanes.find((l) => l.laneKind === laneKind);
}

// ─── DMX lane mutators (redesigned 2026-08-04) ────────────────────
//
// Flat lanes only — no sub-lanes, no group/capability split. `dmx-rig`
// needs nothing beyond the generic `addLane(timelineId, 'dmx-rig')`; a
// `dmx-fixture` lane additionally binds one fixture, so it gets its own
// creator here to resolve the fixture + seed the lane name from it.

/** Add a `dmx-fixture` lane bound to `fixtureId`, named after the fixture.
 *  Auto-numbers like `addLane` when a lane for the same fixture already
 *  exists. Returns the new lane, or null if the fixture isn't found. */
export function addDmxFixtureLane(timelineId: string, fixtureId: string): TimelineLane | null {
  const t = getTimeline(timelineId);
  if (!t) return null;
  // The rig as the app knows it (OPBridge's list once a bridge answered), not
  // only this browser's own library: a light patched on OPBridge silently
  // failed to get a lane (found 2026-10-02 building "Add every light").
  const instance = fixtures().find((f) => f.id === fixtureId) ?? getFixture(fixtureId);
  if (!instance) return null;
  const sameFixtureCount = t.lanes.filter(
    (l) => l.laneKind === 'dmx-fixture' && l.fixtureRef?.fixtureId === fixtureId,
  ).length;
  const name = sameFixtureCount === 0 ? instance.name : `${instance.name} ${sameFixtureCount + 1}`;
  const lane: TimelineLane = {
    id: ulid(), name, laneKind: 'dmx-fixture', steps: [], fixtureRef: { fixtureId },
  };
  upsertTimeline({ ...t, lanes: [...t.lanes, lane] });
  return lane;
}

// ─── Color-marker mutators (fixture-color lane) ───────────────────

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));

export function makeColorMarkerStep(
  position: number,
  color: { r: number; g: number; b: number; w?: number },
  opts: { label?: string; curve?: ValuePointCurve } = {},
): ColorMarkerStep {
  const step: ColorMarkerStep = {
    id: ulid(),
    kind: 'colorMarker',
    position: Math.max(0, position),
    color: {
      r: clamp01(color.r),
      g: clamp01(color.g),
      b: clamp01(color.b),
      ...(color.w !== undefined ? { w: clamp01(color.w) } : {}),
    },
  };
  if (opts.label) step.label = opts.label;
  if (opts.curve && opts.curve !== 'linear') step.curve = opts.curve;
  return step;
}

/** Change the color of an existing color marker (right-click → picker). */
export function setColorMarkerColor(
  timelineId: string,
  laneId: string,
  stepId: string,
  color: { r: number; g: number; b: number; w?: number },
): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  const nextLanes = t.lanes.map((l) =>
    l.id === laneId
      ? {
          ...l,
          steps: l.steps.map((s) => {
            if (s.id !== stepId) return s;
            if (s.kind !== 'colorMarker') return s;
            return {
              ...s,
              color: {
                r: clamp01(color.r),
                g: clamp01(color.g),
                b: clamp01(color.b),
                ...(color.w !== undefined ? { w: clamp01(color.w) } : {}),
              },
            };
          }),
        }
      : l,
  );
  upsertTimeline({ ...t, lanes: nextLanes });
}

/** An OPVisual cell / column cue's Follow switch (2026-10-18, OPVisual
 *  DEC-148): writes the named action's `follow` input, 1 or 0. Off is the
 *  default and is also what a step with no value compiles to
 *  (`bridgeSync.ts::withOpvFollowDefault`). */
/** An OPVisual palette step (2026-10-28): a cue sending `{module: 'opvisual', command: 'palette', args: {id}}`, named
 *  after the palette. */
export function makeOpvPaletteStep(position: number, id: string, name: string): TimelineStep {
  return { id: ulid(), kind: 'cue', position, action: { module: 'opvisual', command: 'palette', args: { id } }, label: name } as TimelineStep;
}

/** The palette id a step sends, or null when it is not an OPVisual palette step. */
export function opvPaletteOf(s: TimelineStep): string | null {
  if (s.kind !== 'cue') return null;
  const a = s.action as { module?: unknown; command?: unknown; args?: { id?: unknown } } | undefined;
  return a?.module === 'opvisual' && a.command === 'palette' && typeof a.args?.id === 'string' ? a.args.id : null;
}

/** Point an OPVisual palette step at another palette. */
export function setOpvPalette(timelineId: string, laneId: string, stepId: string, id: string, name: string): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  upsertTimeline({ ...t, lanes: t.lanes.map((l) => (l.id !== laneId ? l : { ...l, steps: l.steps.map((s) =>
    s.id === stepId && opvPaletteOf(s) !== null ? ({ ...s, action: { module: 'opvisual', command: 'palette', args: { id } }, label: name } as TimelineStep) : s) })) });
}

export function setCueFollow(timelineId: string, laneId: string, stepId: string, follow: boolean): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  const nextLanes = t.lanes.map((l) =>
    l.id === laneId
      ? {
          ...l,
          steps: l.steps.map((s) => {
            if (s.id !== stepId || s.kind !== 'cue') return s;
            const a = s.action as { args?: { values?: Record<string, unknown> } } & Record<string, unknown>;
            const args = a.args ?? {};
            return { ...s, action: { ...a, args: { ...args, values: { ...(args.values ?? {}), follow: follow ? 1 : 0 } } } } as TimelineStep;
          }),
        }
      : l,
  );
  upsertTimeline({ ...t, lanes: nextLanes });
}

/** Change the segment curve out of a color marker (right-click → curve). */
export function setColorMarkerCurve(
  timelineId: string,
  laneId: string,
  stepId: string,
  curve: ValuePointCurve,
): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  const nextLanes = t.lanes.map((l) =>
    l.id === laneId
      ? {
          ...l,
          steps: l.steps.map((s) => {
            if (s.id !== stepId) return s;
            if (s.kind !== 'colorMarker') return s;
            const next = { ...s };
            if (curve === 'linear') delete next.curve;
            else next.curve = curve;
            return next;
          }),
        }
      : l,
  );
  upsertTimeline({ ...t, lanes: nextLanes });
}

/** Change the interpolation space out of a color marker. Passing 'sRGB'
 *  reverts to the default (stored as absent). */
export function setColorMarkerSpace(
  timelineId: string,
  laneId: string,
  stepId: string,
  space: ColorInterpolationSpace,
): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  const nextLanes = t.lanes.map((l) =>
    l.id === laneId
      ? {
          ...l,
          steps: l.steps.map((s) => {
            if (s.id !== stepId) return s;
            if (s.kind !== 'colorMarker') return s;
            const next = { ...s };
            if (space === 'sRGB') delete next.space;
            else next.space = space;
            // sRGB doesn't consult path — drop stale value when reverting.
            if (space === 'sRGB') delete next.path;
            return next;
          }),
        }
      : l,
  );
  upsertTimeline({ ...t, lanes: nextLanes });
}

/** Change the hue-wheel direction out of a color marker. Only meaningful
 *  when space is HSL/OKLCH; the sampler ignores path on sRGB segments. */
export function setColorMarkerPath(
  timelineId: string,
  laneId: string,
  stepId: string,
  path: HuePathDirection,
): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  const nextLanes = t.lanes.map((l) =>
    l.id === laneId
      ? {
          ...l,
          steps: l.steps.map((s) => {
            if (s.id !== stepId) return s;
            if (s.kind !== 'colorMarker') return s;
            const next = { ...s };
            if (path === 'short') delete next.path;
            else next.path = path;
            return next;
          }),
        }
      : l,
  );
  upsertTimeline({ ...t, lanes: nextLanes });
}

/** Set any of a colour keyframe's transition fields (curve, space, path) in
 *  ONE edit, so choosing "Fade" (a curve and a space) is one undo step and one
 *  sync rather than two. Defaults are stored as absent, as the single-field
 *  setters store them: 'linear', 'sRGB' (which also drops `path`), 'short'. */
export function setColorMarkerTransition(
  timelineId: string,
  laneId: string,
  stepId: string,
  change: { curve?: ValuePointCurve; space?: ColorInterpolationSpace; path?: HuePathDirection },
): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  const nextLanes = t.lanes.map((l) =>
    l.id === laneId
      ? {
          ...l,
          steps: l.steps.map((s) => {
            if (s.id !== stepId || s.kind !== 'colorMarker') return s;
            const next = { ...s };
            if (change.curve !== undefined) {
              if (change.curve === 'linear') delete next.curve;
              else next.curve = change.curve;
            }
            if (change.space !== undefined) {
              if (change.space === 'sRGB') { delete next.space; delete next.path; }
              else next.space = change.space;
            }
            if (change.path !== undefined) {
              if (change.path === 'short') delete next.path;
              else next.path = change.path;
            }
            return next;
          }),
        }
      : l,
  );
  upsertTimeline({ ...t, lanes: nextLanes });
}

/** Move a colour keyframe to `position` on `toLaneId` (its own lane or another
 *  lights lane) in ONE edit. A keyframe already on that beat is replaced, the
 *  rule a dropped colour and a paste follow (`overwriteWithMany`): two
 *  keyframes on one beat would leave which colour plays to array order. */
export function moveColorMarker(
  timelineId: string,
  fromLaneId: string,
  toLaneId: string,
  stepId: string,
  position: number,
): void {
  const t = getTimeline(timelineId);
  if (!t) return;
  const moving = t.lanes.find((l) => l.id === fromLaneId)?.steps.find((s) => s.id === stepId);
  if (!moving || moving.kind !== 'colorMarker' || !t.lanes.some((l) => l.id === toLaneId)) return;
  const moved: TimelineStep = { ...moving, position: Math.max(0, position) };
  const nextLanes = t.lanes.map((l) => {
    let steps = l.id === fromLaneId ? l.steps.filter((s) => s.id !== stepId) : l.steps;
    if (l.id === toLaneId) {
      steps = steps.filter((s) => !(s.kind === 'colorMarker' && Math.abs(s.position - moved.position) < 1e-6));
      steps = [...steps, moved];
    }
    return steps === l.steps ? l : { ...l, steps };
  });
  upsertTimeline({ ...t, lanes: nextLanes });
}

// ─── Bulk step operations (for selection-driven flows) ──────────
//
// Selection lives in `selection.ts`; these helpers are what marquee /
// copy-paste / delete-selected call. All wrapped in a single checkpoint
// via beginBatch so the whole operation is one undo entry.

/** Remove a set of steps identified by `{laneId, stepId}` pairs. Cross-
 *  lane — one atomic edit. */
export function removeSteps(timelineId: string, pairs: Array<{ laneId: string; stepId: string }>): void {
  if (pairs.length === 0) return;
  const t = getTimeline(timelineId);
  if (!t) return;
  const byLane = new Map<string, Set<string>>();
  for (const p of pairs) {
    let s = byLane.get(p.laneId);
    if (!s) { s = new Set(); byLane.set(p.laneId, s); }
    s.add(p.stepId);
  }
  const nextLanes = t.lanes.map((l) => {
    const targets = byLane.get(l.id);
    if (!targets || targets.size === 0) return l;
    return { ...l, steps: l.steps.filter((s) => !targets.has(s.id)) };
  });
  upsertTimeline({ ...t, lanes: nextLanes });
}

// ─── Pattern blocks (2026-10-20) ─────────────────────────────────
// Operator: "making the timeline more efficient with the blocks instead of repeating the same patterns. sort of like
// compression". A pattern is one cycle of colour keys repeated for the block's length (schema `PatternStep`).

/** Write a pattern block out as plain keyframes in its place: what it played, now editable one by one. Returns how
 *  many keyframes it wrote. */
export function unpackPattern(timelineId: string, laneId: string, stepId: string): number {
  const t = getTimeline(timelineId);
  const lane = t?.lanes.find((l) => l.id === laneId);
  const pat = lane?.steps.find((x): x is PatternStep => x.id === stepId && x.kind === 'pattern');
  if (!t || !lane || !pat) return 0;
  const keys = unrollPattern(pat).map((k) => ({ ...k, id: ulid() }));
  const end = pat.position + pat.durationBars;
  // The block owned its span, so the keyframes it hid there go: what plays does not change.
  const kept = lane.steps.filter((x) => x.id !== stepId && !((x.kind === 'colorMarker' || x.kind === 'lightRelease') && x.position >= pat.position - 1e-9 && x.position < end - 1e-9));
  upsertTimeline({ ...t, lanes: t.lanes.map((l) => (l.id !== laneId ? l : { ...l, steps: [...kept, ...keys].sort((a, b) => a.position - b.position) })) });
  return keys.length;
}

/** Turn keyframes on one lane into a pattern block that plays them once, from the first of them. Its cycle is
 *  musical: a beat when they all fit in one, else 1, 2, 4 or 8 bars - the length at which the lane's next keyframe
 *  starts the next cycle when there is one (a bar of a groove selected out of a groove), else the shortest that holds
 *  them. Drag the block's end to repeat it. Returns the new block's id, or null when fewer than two keyframes on one
 *  lane were given. */
export function makePattern(timelineId: string, laneId: string, stepIds: readonly string[]): string | null {
  const t = getTimeline(timelineId);
  const lane = t?.lanes.find((l) => l.id === laneId);
  if (!t || !lane) return null;
  const ids = new Set(stepIds);
  const keys = lane.steps.filter((x): x is ColorMarkerStep => ids.has(x.id) && x.kind === 'colorMarker').sort((a, b) => a.position - b.position);
  if (keys.length < 2) return null;
  const beat = 1 / signatureBeats(t);
  const start = keys[0]!.position;
  const span = keys[keys.length - 1]!.position - start;
  const fits = [beat, 1, 2, 4, 8].filter((c) => c > span + 1e-9);
  if (fits.length === 0) return null;
  const next = lane.steps.filter((x) => x.kind === 'colorMarker' && !ids.has(x.id) && x.position > keys[keys.length - 1]!.position + 1e-9).sort((x, y) => x.position - y.position)[0];
  const cycleBars = (next && fits.find((c) => Math.abs(start + c - next.position) < 1e-6)) ?? fits[0]!;
  const pk: PatternKey[] = keys.map((k) => ({
    at: Math.round((k.position - start) * 1e6) / 1e6, color: { ...k.color },
    ...(k.label !== undefined ? { label: k.label } : {}), ...(k.curve !== undefined ? { curve: k.curve } : {}),
    ...(k.space !== undefined ? { space: k.space } : {}), ...(k.path !== undefined ? { path: k.path } : {}),
  }));
  const pat: PatternStep = { id: ulid(), kind: 'pattern', position: start, durationBars: cycleBars, cycleBars, keys: pk };
  upsertTimeline({ ...t, lanes: t.lanes.map((l) => (l.id !== laneId ? l : { ...l, steps: [...l.steps.filter((x) => !ids.has(x.id)), pat].sort((a, b) => a.position - b.position) })) });
  return pat.id;
}

// ─── Editor open/close signal ────────────────────────────────────
// Same pattern as macrosEditorOpen — trigger sites (Header button, future
// InspectorPanel entry when a button binds a timeline) call open/close;
// App.tsx renders the editor off the signal.

const [editorOpenSignal, setEditorOpenSignal] = createSignal(false);
/** Which timeline id is currently being edited. Null = create/pick view. */
const [editorTargetIdSignal, setEditorTargetIdSignal] = createSignal<string | null>(null);
/** WHICH editor is open (DEC-240, operator 2026-10-01: "i need the timeline to
 *  replace the current timeline. make sure to keep and rename the current
 *  timeline to legacy timeline"). `v2` is the timeline editor
 *  (`TimelineV2.tsx`); `legacy` is the first editor (`TimelineEditor.tsx`),
 *  kept and reachable from v2's toolbar and Navigate's search. Both edit the
 *  same timelines through this store, so switching keeps the open timeline. */
export type TimelineEditorKind = 'v2' | 'legacy';
const [editorKindSignal, setEditorKindSignal] = createSignal<TimelineEditorKind>('v2');

export const timelineEditorOpen: Accessor<boolean> = editorOpenSignal;
export const timelineEditorTargetId: Accessor<string | null> = editorTargetIdSignal;
export const timelineEditorKind: Accessor<TimelineEditorKind> = editorKindSignal;

/** Open the timeline editor (v2). */
export function openTimelineEditor(targetId: string | null = null): void {
  setEditorKindSignal('v2');
  setEditorTargetIdSignal(targetId);
  setEditorOpenSignal(true);
}
/** Open the legacy timeline editor (the first one, kept by DEC-240). */
export function openLegacyTimelineEditor(targetId: string | null = null): void {
  setEditorKindSignal('legacy');
  setEditorTargetIdSignal(targetId);
  setEditorOpenSignal(true);
}
/** Swap editors without closing, keeping the timeline that is open. */
export function switchTimelineEditor(kind: TimelineEditorKind): void {
  setEditorKindSignal(kind);
}
export function closeTimelineEditor(): void {
  setEditorOpenSignal(false);
  setEditorTargetIdSignal(null);
}
export function selectTimelineInEditor(id: string | null): void {
  setEditorTargetIdSignal(id);
}

// ─── DEV test hook ───────────────────────────────────────────────────────────
// Mirrors __opbuttonsBridges / __opbuttonsConsumer: a DEV-only handle so bot
// smokes can seed a timeline + open the editor deterministically and read back
// the store's in-memory state (lane count, loop flag) after driving the UI.
// Stripped from production by the import.meta.env.DEV gate.
declare global {
  interface Window {
    __opbuttonsTimeline?: {
      create(tier: TimelineTier, name: string): string;
      /** Opens the LEGACY editor: every smoke written before DEC-240 drives
       *  that editor's DOM, so this hook kept its meaning. */
      open(id: string | null): void;
      /** Opens the timeline editor v2 (DEC-240). */
      openV2(id: string | null): void;
      close(): void;
      list(): Timeline[];
      laneCount(id: string): number;
      loops(id: string): boolean;
      firstPlainLaneId(id: string): string | null;
      /** Added 2026-08-16 for the OPBridge dispatch verification pass —
       *  lets a smoke build a fireable timeline (lane + buttonRef step)
       *  without driving the drag-drop palette UI. */
      addLane(timelineId: string, laneKind: TimelineLaneKind): string | null;
      addButtonRefStep(timelineId: string, laneId: string, buttonId: string, position: number): string | null;
      /** Added 2026-09-29 for the overwrite smoke: read a lane's steps, and
       *  give a block a length without driving the resize handle. */
      steps(timelineId: string, laneId: string): Array<{ id: string; buttonId?: string; position: number; durationBars?: number }>;
      setStepDuration(timelineId: string, laneId: string, stepId: string, bars: number): void;
    };
  }
}

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.__opbuttonsTimeline = {
    create: (tier, name) => createTimeline(tier, name).id,
    open: (id) => openLegacyTimelineEditor(id),
    openV2: (id) => openTimelineEditor(id),
    close: () => closeTimelineEditor(),
    list: () => timelines(),
    laneCount: (id) => getTimeline(id)?.lanes.length ?? -1,
    loops: (id) => { const t = getTimeline(id); return t ? timelineLoops(t) : false; },
    firstPlainLaneId: (id) => {
      const t = getTimeline(id);
      if (!t) return null;
      const plain = t.lanes.find((l) => {
        const k = l.laneKind ?? 'mixed';
        return k === 'midi' || k === 'osc' || k === 'marker' || k === 'mixed';
      });
      return plain?.id ?? null;
    },
    addLane: (timelineId, laneKind) => addLane(timelineId, laneKind)?.id ?? null,
    addButtonRefStep: (timelineId, laneId, buttonId, position) =>
      addStep(timelineId, laneId, makeButtonRefStep(buttonId, position)),
    steps: (timelineId, laneId) =>
      (getTimeline(timelineId)?.lanes.find((l) => l.id === laneId)?.steps ?? []).map((s) => ({
        id: s.id,
        ...(isButtonRefStep(s) ? { buttonId: s.buttonId } : {}),
        position: s.position,
        ...(typeof (s as { durationBars?: number }).durationBars === 'number'
          ? { durationBars: (s as { durationBars?: number }).durationBars }
          : {}),
      })),
    setStepDuration: (timelineId, laneId, stepId, bars) => setStepDuration(timelineId, laneId, stepId, bars),
  };
}
